import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  Mail,
  MapPin,
  Phone,
  Printer,
  QrCode,
  Settings2,
} from "lucide-react";

import { apiService } from "../../services/api";
import adminApi from "../../services/adminApi";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

import CockpitHome from "./CockpitHome";
import {
  AdaptiveSubnav,
  CockpitPanel,
  CockpitTabs,
  CockpitWorkspace,
  CockpitWorkspaceContent,
} from "./cockpit";
import { adminRoutes } from "./navigation/adminRoutes";
import { getRememberedCockpitPath } from "./navigation/adminPlaceMemory";
import { canAccessSection, getAdminUser } from "./navigation/sectionAccess";

const DAYS = [
  { id: "monday", label: "Monday", shortLabel: "Mon" },
  { id: "tuesday", label: "Tuesday", shortLabel: "Tue" },
  { id: "wednesday", label: "Wednesday", shortLabel: "Wed" },
  { id: "thursday", label: "Thursday", shortLabel: "Thu" },
  { id: "friday", label: "Friday", shortLabel: "Fri" },
  { id: "saturday", label: "Saturday", shortLabel: "Sat" },
  { id: "sunday", label: "Sunday", shortLabel: "Sun" },
];

const normalizeSchedule = (schedule) => {
  if (Array.isArray(schedule)) return schedule;
  if (!schedule || typeof schedule !== "object") return [];

  return Object.entries(schedule).map(([day, value]) => ({
    day,
    time_slots: Array.isArray(value) ? value : (value?.time_slots ?? []),
  }));
};

const isAvailabilityPlaceholder = (slot) =>
  !slot.program_id &&
  Boolean(slot.note || slot.notes) &&
  slot.start_time === "08:00" &&
  slot.end_time === "20:00";

const classSlotsFor = (daySchedule) =>
  (daySchedule?.time_slots ?? []).filter(
    (slot) => !isAvailabilityPlaceholder(slot),
  );

const formatTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return time;

  const suffix = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

const formatSlot = (slot) =>
  `${formatTime(slot.start_time)}–${formatTime(slot.end_time)}`;

const ScheduleSummary = ({ schedule, today }) => (
  <Card className="admin-card">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <CalendarDays className="h-5 w-5 text-amber-600" />
        This week&apos;s class schedule
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="divide-y divide-stone-200 dark:divide-stone-700">
        {DAYS.map((day) => {
          const daySchedule = schedule.find(
            (item) => item.day?.toLowerCase() === day.id,
          );
          const slots = classSlotsFor(daySchedule).sort((left, right) =>
            left.start_time.localeCompare(right.start_time),
          );
          const isToday = day.id === today;

          return (
            <div
              key={day.id}
              className={`grid grid-cols-[3rem_1fr] gap-3 py-2.5 text-sm ${
                isToday ? "font-medium text-red-800 dark:text-amber-300" : ""
              }`}
            >
              <span aria-label={day.label}>{day.shortLabel}</span>
              {slots.length > 0 ? (
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {slots.map((slot, index) => (
                    <span key={slot.id ?? `${slot.start_time}-${index}`}>
                      {formatSlot(slot)}
                      {(slot.program_name || slot.note || slot.notes) && (
                        <span className="text-stone-500 dark:text-stone-400">
                          {" "}
                          · {slot.program_name || slot.note || slot.notes}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-stone-500 dark:text-stone-400">
                  No classes
                </span>
              )}
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

const BusinessAtAGlance = ({ businessInfo }) => (
  <Card className="admin-card">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <Clock className="h-5 w-5 text-amber-600" />
        Business info at a glance
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4 text-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Business hours
        </p>
        <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">
          {businessInfo?.hours || "Not published"}
        </p>
      </div>

      <div className="space-y-2 border-t border-stone-200 pt-4 dark:border-stone-700">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Primary contact
        </p>
        {businessInfo?.phone && (
          <a
            className="flex items-center gap-2 hover:text-red-800 dark:hover:text-amber-300"
            href={`tel:${businessInfo.phone}`}
          >
            <Phone className="h-4 w-4 text-stone-500" />
            {businessInfo.phone}
          </a>
        )}
        {businessInfo?.email && (
          <a
            className="flex items-center gap-2 break-all hover:text-red-800 dark:hover:text-amber-300"
            href={`mailto:${businessInfo.email}`}
          >
            <Mail className="h-4 w-4 shrink-0 text-stone-500" />
            {businessInfo.email}
          </a>
        )}
        {businessInfo?.address && (
          <p className="flex items-start gap-2 text-stone-600 dark:text-stone-300">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" />
            {businessInfo.address}
          </p>
        )}
        {!businessInfo && (
          <p className="text-stone-500 dark:text-stone-400">
            Business information is unavailable.
          </p>
        )}
      </div>
    </CardContent>
  </Card>
);

const getSubsectionId = (section, search) => {
  if (!section?.subSections?.length) return null;
  const requestedView = new URLSearchParams(search).get("view");
  return section.subSections.some((subsection) => subsection.id === requestedView)
    ? requestedView
    : section.subSections[0].id;
};

const BusinessCockpitHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser],
  );
  const sections = useMemo(
    () => adminRoutes.business.sections.filter((section) => hasAccess(section.id)),
    [hasAccess],
  );
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const activeSection = sections.find((section) => section.path === normalizedPath);
  const activeSubsectionId = getSubsectionId(activeSection, location.search);
  const activeSubsection = activeSection?.subSections?.find(
    (subsection) => subsection.id === activeSubsectionId,
  );

  const [businessInfo, setBusinessInfo] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailures, setLoadFailures] = useState({
    schedule: false,
    qrCodes: false,
  });

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      const [businessResult, scheduleResult, qrResult] =
        await Promise.allSettled([
          apiService.getBusinessInfo(),
          apiService.getSchedule(),
          adminApi.get("/api/admin/qr-codes").then(async (response) => {
            if (!response.ok) throw new Error("Failed to load QR codes");
            return response.json();
          }),
        ]);

      if (!active) return;

      if (businessResult.status === "fulfilled")
        setBusinessInfo(businessResult.value);
      if (scheduleResult.status === "fulfilled") {
        setSchedule(normalizeSchedule(scheduleResult.value));
      }
      if (qrResult.status === "fulfilled") {
        setQrCodes(Array.isArray(qrResult.value) ? qrResult.value : []);
      }
      setLoadFailures({
        schedule: scheduleResult.status === "rejected",
        qrCodes: qrResult.status === "rejected",
      });
      setLoading(false);
    };

    loadDashboard();
    return () => {
      active = false;
    };
  }, []);

  const today = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const todaySchedule = schedule.find(
    (item) => item.day?.toLowerCase() === today,
  );
  const todayClassCount = classSlotsFor(todaySchedule).length;
  const weeklyClassCount = useMemo(
    () => schedule.reduce((total, day) => total + classSlotsFor(day).length, 0),
    [schedule],
  );
  const enabledQrCount = qrCodes.filter((qrCode) => qrCode.enabled).length;

  const kpis = [
    {
      id: "today-classes",
      title: "Today's classes",
      value: loading
        ? "—"
        : loadFailures.schedule
          ? "Unavailable"
          : todayClassCount,
      subtitle: today.charAt(0).toUpperCase() + today.slice(1),
      icon: CalendarDays,
    },
    {
      id: "weekly-classes",
      title: "Classes this week",
      value: loading
        ? "—"
        : loadFailures.schedule
          ? "Unavailable"
          : weeklyClassCount,
      subtitle: "Recurring weekly schedule",
      icon: Clock,
    },
    {
      id: "business-hours",
      title: "Business hours",
      value: loading ? "—" : businessInfo?.hours ? "Published" : "Missing",
      subtitle: businessInfo?.hours || "Add hours in Business Info",
      icon: Settings2,
    },
    {
      id: "qr-codes",
      title: "Active QR codes",
      value: loading
        ? "—"
        : loadFailures.qrCodes
          ? "Unavailable"
          : enabledQrCount,
      subtitle: loadFailures.qrCodes
        ? "Could not load QR codes"
        : `${qrCodes.length} configured`,
      icon: QrCode,
    },
  ];

  const quickActions = [
    {
      id: "edit-hours",
      label: "Edit hours",
      icon: Clock,
      path: "/admin/business/info",
      sectionId: "business",
    },
    {
      id: "edit-schedule",
      label: "Edit schedule",
      icon: CalendarDays,
      path: "/admin/business/schedule",
      sectionId: "schedule",
    },
    {
      id: "generate-qr",
      label: "Generate QR",
      icon: QrCode,
      path: "/admin/business/qr-codes",
      sectionId: "qrcodes",
    },
    {
      id: "print-qr",
      label: "Print QR codes",
      icon: Printer,
      path: "/admin/business/qr-codes",
      sectionId: "qrcodes",
    },
  ];

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath("business", sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const tabPanelId = `business-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="business"
      description="Today’s classes, contact details, and customer-ready QR codes."
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      overview={(
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ScheduleSummary schedule={schedule} today={today} />
          </div>
          <BusinessAtAGlance businessInfo={businessInfo} />
        </div>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(section.path);
        }}
        label="Business and operations sections"
        idPrefix="business-cockpit-tab"
      />

      <CockpitPanel
        id={tabPanelId}
        aria-labelledby={`business-cockpit-tab-${activeSection.id}`}
      >
        {activeSection.subSections?.length ? (
          <CockpitWorkspace>
            <AdaptiveSubnav
              tabs={activeSection.subSections}
              active={activeSubsectionId}
              onChange={(subsectionId) => {
                const subsection = activeSection.subSections.find(
                  (candidate) => candidate.id === subsectionId,
                );
                if (subsection) navigate(subsection.path);
              }}
              label={`${activeSection.label} views`}
              idPrefix={`business-${activeSection.id}-view`}
            />
            <CockpitWorkspaceContent
              role="tabpanel"
              id={`business-${activeSection.id}-view-panel-${activeSubsectionId}`}
              aria-labelledby={`business-${activeSection.id}-view-${activeSubsectionId}`}
            >
              <Outlet context={{ activeSection, activeSubsection }} />
            </CockpitWorkspaceContent>
          </CockpitWorkspace>
        ) : (
          <CockpitWorkspaceContent>
            <Outlet context={{ activeSection, activeSubsection: null }} />
          </CockpitWorkspaceContent>
        )}
      </CockpitPanel>
    </CockpitHome>
  );
};

export default BusinessCockpitHome;
